import React, { useState, useEffect, useMemo } from 'react';
import { Box, Popper, ClickAwayListener } from '@mui/material';
import { TextField, EsgSingleSelectControlledField, BasicButton } from 'components';
import { useNotify, useGetAllowedValues, useGetConfigParameter } from 'hooks';
import { addScorecardCellData, updateScorecardCellData, ScorecardValue, AddScorecardCellDataRequest, UpdateScorecardCellDataRequest } from 'api';
import { getDateRangeFromTimeframe } from 'utils';
import { SaveIcon } from 'assets';

interface EditCellValueModalProps {
    isOpen: boolean;
    onClose: () => void;
    cellData: ScorecardValue;
    rowId: number;
    availableUnits: string[];
    currentTimeframe: string;
    onSuccess: () => void;
    anchorEl: HTMLElement | null;
    isCostRow?: boolean;
    defaultUnit?: string;
    defaultCurrency?: string;
}

export const EditCellValueModal: React.FC<EditCellValueModalProps> = ({
    isOpen,
    onClose,
    cellData,
    rowId,
    availableUnits,
    currentTimeframe,
    onSuccess,
    anchorEl,
    isCostRow = false,
    defaultUnit,
    defaultCurrency,
}) => {
    const [value, setValue] = useState<string>('');
    const [selectedUnit, setSelectedUnit] = useState<string>('');
    const [isLoading, setIsLoading] = useState(false);
    const [disableClickAway, setDisableClickAway] = useState(false);
    const notify = useNotify();
    const { data: allowedValues } = useGetAllowedValues();
    const { data: configParameter } = useGetConfigParameter();

    // Memoize currencies to avoid unnecessary re-renders
    const currencies = useMemo(() => allowedValues?.currencies || [defaultCurrency], [allowedValues?.currencies, defaultCurrency]);

    // Get company months from config parameter for fiscal year calculation
    const companyMonths = useMemo(() => {
        const months = configParameter
            ?.filter((param: any) => param.name.includes("company_month"))
            .map((param: any) => param.configValue);
        return (months && months.length > 0) ? months : ["Jan", "Dec"];
    }, [configParameter]);

    // Check if there are any changes from the original data
    const hasChanges = () => {
        const originalValue = cellData.value?.toString() || '';
        const originalUnit = cellData.unit || (isCostRow ? defaultCurrency : defaultUnit) || '';
        return value !== originalValue || selectedUnit !== originalUnit;
    };

    useEffect(() => {
        if (isOpen) {
            setValue(cellData.value?.toString() || '');
            const defaultUnitValue = isCostRow ? defaultCurrency : defaultUnit;
            setSelectedUnit(cellData.unit || defaultUnitValue || '');
        }
    }, [isOpen, cellData, isCostRow, defaultUnit, defaultCurrency]);

    // Handle dropdown interactions
    useEffect(() => {
        const handleMouseDown = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            // Check if clicking on dropdown elements
            if (target && (
                target.closest('[data-testid="unit-dropdown"]') ||
                target.closest('[data-testid="esg-single-select-container"]') ||
                target.closest('.MuiSelect-select') ||
                target.closest('.MuiSelect-root') ||
                target.closest('.MuiInputBase-root') ||
                target.closest('[role="combobox"]')
            )) {
                setDisableClickAway(true);
            }
        };

        const handleMouseUp = () => {
            // Re-enable click away after a short delay
            setTimeout(() => {
                setDisableClickAway(false);
            }, 100);
        };

        document.addEventListener('mousedown', handleMouseDown);
        document.addEventListener('mouseup', handleMouseUp);

        return () => {
            document.removeEventListener('mousedown', handleMouseDown);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, []);


    const handleValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = event.target.value;
        // Allow only numbers, dots, commas, and negative sign
        if (/^-?[\d.,]*$/.test(inputValue)) {
            setValue(inputValue);
        }
    };

    const handleSave = async () => {
        if (!value.trim()) {
            notify.error('Please enter a value');
            return;
        }

        if (!selectedUnit) {
            notify.error('Please select a unit');
            return;
        }

        setIsLoading(true);

        try {
            const numericValue = parseFloat(value.replace(',', '.'));

            if (Number.isNaN(numericValue)) {
                notify.error('Please enter a valid number');
                return;
            }

            const { startDate, endDate } = getDateRangeFromTimeframe(cellData.timeframeGroup, currentTimeframe, companyMonths);

            // Always send both value and unit, using current values or previous ones
            const valueToSend = numericValue;
            const unitToSend = selectedUnit;

            // Create payload based on row type
            const payload = isCostRow ? {
                rowId,
                startDate,
                endDate,
                costValue: valueToSend,
                costUnit: unitToSend,
                costData: cellData.scorecardDataId ? true : "true", // boolean for PUT, string for POST
                inputTimeframe: currentTimeframe
            } : {
                rowId,
                startDate,
                endDate,
                value: valueToSend,
                unit: unitToSend,
                costData: cellData.scorecardDataId ? false : "false", // boolean for PUT, string for POST
                inputTimeframe: currentTimeframe
            };

            if (cellData.scorecardDataId) {
                // Update existing cell data
                await updateScorecardCellData(cellData.scorecardDataId, payload as UpdateScorecardCellDataRequest);
            } else {
                // Add new cell data
                await addScorecardCellData(payload as AddScorecardCellDataRequest);
            }

            notify.success('Data saved successfully');
            onSuccess();
            onClose();
        } catch (error: any) {
            const errorMessage = error?.response?.data || error?.message || 'Failed to save data';
            notify.error(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    // Get options based on row type
    const unitOptions = (isCostRow ? currencies : availableUnits)
        .filter((unit): unit is string => Boolean(unit)) // Type guard to ensure string
        .map(unit => ({
            value: unit,
            label: unit
        }));

    const handleClickAway = (event: MouseEvent | TouchEvent) => {
        if (disableClickAway) {
            return;
        }

        const target = event.target as HTMLElement;

        if (target && target.closest('[data-testid="edit-cell-modal"]')) {
            return;
        }

        if (target && target.closest('[data-testid="delete-cell-button"]')) {
            return;
        }

        onClose();
    };

    return (
        <Popper
            open={isOpen}
            anchorEl={anchorEl}
            placement="bottom-start"
            sx={{
                zIndex: 1300,
                width: anchorEl?.getBoundingClientRect?.()?.width ? (anchorEl.getBoundingClientRect().width + 16) : '200px',
                marginLeft: '-8px !important'
            }}
        >
            <ClickAwayListener onClickAway={handleClickAway}>
                <Box
                    data-testid="edit-cell-modal"
                    sx={{
                        borderRadius: '4px',
                        border: '2px solid #494EEE',
                        boxShadow: '2px 2px 2px 0 rgba(0, 0, 0, 0.10)',
                        padding: '16px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '13px',
                        backgroundColor: 'white'
                    }}
                >
                    <Box sx={{ display: 'flex', gap: '4px' }}>
                        <TextField
                            label="Value"
                            value={value}
                            onChange={handleValueChange}
                            disabled={isLoading}
                            placeholder="Enter numeric value"
                            sx={{
                                '& .MuiInputBase-root': {
                                    height: '40px'
                                },
                                '& .MuiInputLabel-root': {
                                    transform: 'translate(14px, -9px) scale(0.75)',
                                    '&.Mui-focused': {
                                        transform: 'translate(14px, -9px) scale(0.75)'
                                    }
                                }
                            }}
                        />

                        <EsgSingleSelectControlledField
                            label="Unit"
                            selectedValue={selectedUnit}
                            setSelectedValue={(selectedValue) => setSelectedUnit(selectedValue as string)}
                            options={unitOptions}
                            disabled={isLoading}
                            width="50%"
                            height="40px"
                            data-testid="unit-dropdown"
                            sxSelect={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "4px",
                                    height: "40px",
                                }

                            }}
                            sxLabel={{
                                marginBottom: "5px"
                            }}
                            sxPoppover={{
                                width: "fit-content",
                            }}
                        />
                    </Box>

                    <BasicButton
                        variant="contained"
                        onClick={handleSave}
                        disabled={isLoading || !value.trim() || !selectedUnit || !hasChanges()}
                        startIcon={<SaveIcon />}
                        sx={{
                            alignSelf: 'flex-end',
                            width: '50%',
                            color: 'white',
                            marginTop: '0px',
                            marginRight: '0px',
                            ":disabled": {
                                border: 0
                            }
                        }}
                    >
                        Save
                    </BasicButton>
                </Box>
            </ClickAwayListener>
        </Popper>
    );
};
